import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  Bookmark,
  MapPin,
  CalendarDays,
  Tag,
  ExternalLink,
  Share2,
  CalendarPlus,
  Apple,
  Navigation,
  Ticket,
  Globe,
  Instagram,
  Facebook,
  Building2,
  Flag,
  Info,
} from "lucide-react";
import { MapContainer, Marker, useMap } from "react-leaflet";
import { MapTiles } from "@/components/MapTiles";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { fetchEvent, fetchOrganiser, trackView } from "../lib/events";
import { formatDetailDate } from "../lib/dates";
import { countryName } from "../lib/countries";
import { useSaved } from "../hooks/useSaved";
import { useEventCover } from "../hooks/useEventCover";
import { Skeleton } from "../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { googleCalendarUrl, downloadIcs } from "../lib/calendar";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { LikeButton } from "../components/LikeButton";
import { supabase } from "../lib/supabaseClient";

const pinIcon = L.divIcon({
  className: "rr-marker",
  html: '<div class="rr-pin"></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

// Leaflet renders a grey box when the map is initialised before its container
// has its final size (common inside cards/dropdowns). Force a size recalculation
// after mount and on resize so tiles load and the marker sits correctly.
function MiniMapFix({ center }) {
  const map = useMap();
  useEffect(() => {
    const fix = () => {
      map.invalidateSize();
      if (center) map.setView(center, map.getZoom());
    };
    const t1 = setTimeout(fix, 200);
    const t2 = setTimeout(fix, 600);
    window.addEventListener("resize", fix);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", fix);
    };
  }, [map, center]);
  return null;
}

export default function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // When opened from a card/pin, EventDetails renders as a full-screen overlay
  // above the still-mounted originating page (backgroundLocation set). Direct/deep
  // links have no backgroundLocation and render as a normal standalone page.
  const isOverlay = !!(location.state && location.state.backgroundLocation);
  const overlayClass = isOverlay
    ? "fixed inset-0 z-[1500] mx-auto max-w-md overflow-y-auto overscroll-contain bg-[#050505] md:max-w-none"
    : "";
  const { resolved } = useTheme();
  const { isSaved, toggle } = useSaved();
  const [event, setEvent] = useState(null);
  const [organiser, setOrganiser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [reported, setReported] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [ownerIsAdmin, setOwnerIsAdmin] = useState(false);
  const [showAllDates, setShowAllDates] = useState(false);
  const { src: heroSrc, onError: onHeroError } = useEventCover(event, true);

  // Events are visible to any signed-in user except the event's own owner.
  // Most RoadRnr events are scraper-imported with no owner_id at all, so this
  // must not require an owner to exist (support #279162).
  const canReport =
    !!user && !!event && event.owner_id !== user.id;

  const reportEvent = async () => {
    if (!user || !event) return;
    setReporting(true);
    try {
      const { error } = await supabase
        .from("Event_Reports")
        .insert({ event_id: event.id, reporter_user_id: user.id });
      if (error) {
        if (error.code === "23505") {
          setReported(true);
          toast("You've already reported this event.");
        } else {
          toast.error(error.message || "Could not report this event.");
        }
        return;
      }
      setReported(true);
      toast.success("Thanks, this event has been reported.");
    } catch (err) {
      toast.error(err?.message || "Could not report this event.");
    } finally {
      setReporting(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setOrganiser(null);
    setOwnerIsAdmin(false);
    fetchEvent(id)
      .then((ev) => {
        setEvent(ev);
        if (ev?.owner_id) {
          fetchOrganiser(ev.owner_id).then(setOrganiser).catch(() => setOrganiser(null));
        }
        // Hide "Report event" when the owner is a RoadRnr admin. Use the RESOLVED
        // event id so this works for both UUID and slug URLs.
        if (ev?.id) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${ev.id}/owner-admin`)
            .then((r) => r.json())
            .then((d) => setOwnerIsAdmin(!!d?.is_admin))
            .catch(() => setOwnerIsAdmin(false));
        }
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
    trackView(id);
  }, [id]);

  // Event pages show the actual event name in the tab/share title (site name: RoadRnr)
  // and a unique meta description so each event page is distinct for search engines.
  useEffect(() => {
    if (!event?.name) return;
    const descEl = document.head.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;
    document.title = `${event.name} — RoadRnr`;
    const where = event.venue_name || event.address || "";
    const kind = (event.category || "automotive").toLowerCase();
    const desc = `${event.name}${where ? ` at ${where}` : ""} on RoadRnr. Find the date, location, tickets and details for this ${kind} event, save it and add it to your calendar.`;
    if (descEl) descEl.setAttribute("content", desc.slice(0, 300));
    return () => {
      document.title = "RoadRnr — Car & Motorcycle Events";
      if (descEl && prevDesc != null) descEl.setAttribute("content", prevDesc);
    };
  }, [event?.name, event?.venue_name, event?.address, event?.category]);

  if (loading) {
    return (
      <div className={`min-h-[100dvh] p-5 ${overlayClass}`}>
        <Skeleton className="h-[45vh] w-full rounded-3xl bg-white/5" />
        <Skeleton className="mt-5 h-8 w-2/3 rounded-full bg-white/5" />
        <Skeleton className="mt-3 h-4 w-1/2 rounded-full bg-white/5" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className={`flex min-h-[100dvh] flex-col items-center justify-center px-6 text-center ${overlayClass}`}>
        <p className="font-display text-xl font-bold">Event not found</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 rounded-full bg-[#ff2a00] px-6 py-3 text-sm font-semibold"
        >
          Back to Discover
        </button>
      </div>
    );
  }

  const saved = isSaved(event.id);
  const hasCoords =
    typeof event.latitude === "number" && typeof event.longitude === "number";
  const destParam = hasCoords
    ? `${event.latitude},${event.longitude}`
    : encodeURIComponent(event.location || "");

  const { dateLabel, timeLabel } = formatDetailDate(event);

  // Location details (only non-empty parts)
  const locationTitle = event.venue_name || event.location;
  const addressLines = [
    event.address,
    event.postcode,
    event.country_code ? countryName(event.country_code) : "",
  ].filter(Boolean);

  // Event-specific links (exclude ticket — handled by the bottom action)
  const links = [
    event.website_url && { key: "website", label: "Official Website", icon: Globe, url: event.website_url },
    event.instagram_url && { key: "instagram", label: "Instagram", icon: Instagram, url: event.instagram_url },
    event.facebook_url && { key: "facebook", label: "Facebook", icon: Facebook, url: event.facebook_url },
  ].filter(Boolean);

  // Bottom primary action logic
  const hasTicket = !!event.ticket_url;
  const ticketLabel = event.is_free ? "Book / Register" : "Get Tickets";
  // Free events never show the ticket/booking bar even if a URL happens to exist;
  // only paid events with a valid ticket URL do.
  const showBottomBar = hasTicket && !event.is_free;

  const handleShare = async () => {
    const shareUrl = event.slug
      ? `${window.location.origin}/event/${event.slug}`
      : window.location.href;
    const shareData = {
      title: event.name,
      text: `Check out ${event.name} on RoadRnr — ${event.date}`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard");
      }
    } catch (e) {
      if (e && e.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(window.location.href);
          toast.success("Link copied to clipboard");
        } catch {
          toast.error("Could not share this event");
        }
      }
    }
  };

  // Hand off directions to the device's own maps/navigation stack instead of
  // hard-coding Google Maps: Android uses a geo: URI (lets the OS show its
  // maps/nav app chooser), iOS opens its default Maps handler, and desktop/other
  // falls back to web maps directions.
  const openDirections = () => {
    const ua = navigator.userAgent || navigator.vendor || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);
    const label = encodeURIComponent(event.name || locationTitle || "Destination");
    if (isAndroid) {
      const addr = [event.venue_name, event.address, event.postcode].filter(Boolean).join(", ");
      window.location.href = addr
        ? `geo:0,0?q=${encodeURIComponent(addr)}`
        : hasCoords
        ? `geo:${event.latitude},${event.longitude}?q=${event.latitude},${event.longitude}(${label})`
        : `geo:0,0?q=${destParam}`;
      return;
    }
    if (isIOS) {
      const addr = [event.venue_name, event.address, event.postcode].filter(Boolean).join(", ");
      window.location.href = `maps://?daddr=${addr ? encodeURIComponent(addr) : destParam}`;
      return;
    }
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destParam}`,
      "_blank"
    );
  };

  return (
    <div data-testid="event-details-page" className={`${isOverlay ? "" : "relative"} min-h-[100dvh] ${overlayClass}`}>
      <div className={`relative z-0 ${showBottomBar ? "pb-40" : "pb-12"} md:mx-auto md:flex md:max-w-6xl md:items-start md:gap-8 md:px-6 md:pt-6`}>
        {/* Hero */}
        <div className="relative h-[52vh] min-h-[380px] w-full md:sticky md:top-6 md:h-[70vh] md:max-h-[calc(100dvh-10rem)] md:min-h-[320px] md:w-1/2 md:shrink-0 md:overflow-hidden md:rounded-3xl md:border md:border-white/10">
          <img
            src={heroSrc}
            alt={event.name}
            onError={onHeroError}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-[#050505]/40" />

          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-5">
            <button
              data-testid="back-btn"
              onClick={() => {
                if (window.history.state && window.history.state.idx > 0) navigate(-1);
                else navigate("/");
              }}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#050505]/60 backdrop-blur-xl transition-transform duration-200 active:scale-90"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
            <button
              data-testid="details-save-toggle"
              onClick={() => toggle(event.id)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#050505]/60 backdrop-blur-xl transition-transform duration-200 active:scale-90"
            >
              <Bookmark
                className={`h-5 w-5 transition-colors ${
                  saved ? "fill-[#ff2a00] text-[#ff2a00]" : "text-white"
                }`}
              />
            </button>
          </div>

          <div className="rr-on-image absolute inset-x-0 bottom-0 px-5 pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#ff2a00] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                {event.category}
              </span>
              <span
                data-testid="status-pill"
                className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                  event.is_free
                    ? "border border-[#ff2a00]/40 bg-[#ff2a00]/15 text-[#ff2a00]"
                    : "border border-white/20 bg-black/40 text-white"
                }`}
              >
                {event.is_free ? "Free" : "Paid"}
              </span>
              {event.live && (
                <span
                  data-testid="live-pill"
                  className="flex items-center gap-1 rounded-full bg-[#ffb000] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-black"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-black animate-pulse" />
                  Live now
                </span>
              )}
            </div>
            <h1 className="font-display mt-3 text-3xl font-extrabold leading-tight tracking-tight">
              {event.name}
            </h1>
          </div>
        </div>

        {/* Info */}
        <div className="mx-auto max-w-md px-5 md:mx-0 md:w-1/2 md:max-w-none md:px-0">
          <div className="mt-5 space-y-3 rounded-3xl border border-white/10 bg-[#121212] p-5">
            <InfoRow
              icon={CalendarDays}
              label="Date & Time"
              value={dateLabel}
              sub={timeLabel}
            />
            {locationTitle && (
              <>
                <div className="h-px bg-white/5" />
                <InfoRow icon={MapPin} label="Venue" value={locationTitle} />
              </>
            )}
            <div className="h-px bg-white/5" />
            <InfoRow icon={Tag} label="Category" value={event.category} />
          </div>

          {event.description && (
            <div className="mt-6">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                About this event
              </h2>
              <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-white/70">
                {event.description}
              </p>
            </div>
          )}

          {/* Additional/recurring dates — compact, scannable grid (finished ones excluded) */}
          {Array.isArray(event.occurrences) && event.occurrences.length > 0 && (
            <div className="mt-6" data-testid="more-dates">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                More dates
              </h2>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(showAllDates ? event.occurrences : event.occurrences.slice(0, 4)).map((o, i) => {
                  const sp = String(o.start_at).split("T");
                  const ep = String(o.end_at).split("T");
                  const st = (sp[1] || "").slice(0, 5);
                  const et = (ep[1] || "").slice(0, 5);
                  const { dateLabel, timeLabel } = formatDetailDate({
                    start_date: sp[0] || "",
                    start_time: st === "00:00" ? "" : st,
                    end_date: ep[0] || "",
                    end_time: et === "00:00" ? "" : et,
                  });
                  return (
                    <div
                      key={i}
                      data-testid={`more-date-${i}`}
                      className="rounded-xl border border-white/10 bg-[#121212] px-3 py-2"
                    >
                      <p className="truncate text-xs font-semibold text-white">{dateLabel}</p>
                      {timeLabel && <p className="text-[11px] text-white/50">{timeLabel}</p>}
                    </div>
                  );
                })}
              </div>
              {event.occurrences.length > 4 && (
                <button
                  data-testid={showAllDates ? "show-less-dates" : "show-more-dates"}
                  onClick={() => setShowAllDates((v) => !v)}
                  className="mt-3 text-xs font-semibold text-[#ff2a00] transition-colors hover:text-[#ff5a33]"
                >
                  {showAllDates ? "Show less dates" : `Show more dates (${event.occurrences.length - 4})`}
                </button>
              )}
            </div>
          )}

          {/* Location preview + directions */}
          <div className="mt-6">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
              Location
            </h2>
            <button
              data-testid="location-preview"
              onClick={openDirections}
              className="relative mt-2 block w-full overflow-hidden rounded-3xl border border-white/10 text-left transition-transform duration-200 active:scale-[0.99]"
            >
                  <div className="rr-minimap pointer-events-none relative z-0 h-40 w-full overflow-hidden [isolation:isolate]">
                    {hasCoords ? (
                      <MapContainer
                        center={[event.latitude, event.longitude]}
                        zoom={14}
                        zoomControl={false}
                        attributionControl={false}
                        dragging={false}
                        doubleClickZoom={false}
                        scrollWheelZoom={false}
                        touchZoom={false}
                        boxZoom={false}
                        keyboard={false}
                        className="h-full w-full"
                      >
                        <MapTiles theme={resolved} />
                        <Marker
                          position={[event.latitude, event.longitude]}
                          icon={pinIcon}
                        />
                        <MiniMapFix center={[event.latitude, event.longitude]} />
                      </MapContainer>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[#1a1a1a]">
                        <MapPin className="h-6 w-6 text-white/25" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-start gap-3 bg-[#121212] px-4 py-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#ff2a00]" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-medium text-white">
                        {locationTitle}
                      </p>
                      {addressLines.length > 0 && (
                        <p className="text-[12px] leading-snug text-white/50">
                          {addressLines.join(", ")}
                        </p>
                      )}
                      <p className="mt-0.5 text-[12px] text-[#ff2a00]/90">
                        Tap for directions
                      </p>
                    </div>
                    <Navigation className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[#ff2a00]" />
                  </div>
                </button>
          </div>

          {/* Organiser */}
          {organiser && (
            <div className="mt-6" data-testid="organiser-section">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                Organiser
              </h2>
              <div className="mt-2 flex items-center gap-3 rounded-3xl border border-white/10 bg-[#121212] p-4">
                {organiser.imageUrl ? (
                  <img
                    src={organiser.imageUrl}
                    alt={organiser.name || "Organiser"}
                    className="h-12 w-12 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#1e1e1e]">
                    <Building2 className="h-5 w-5 text-[#ff2a00]" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  {organiser.name && (
                    <p className="truncate text-[15px] font-semibold text-white">
                      {organiser.name}
                    </p>
                  )}
                  {organiser.bio && (
                    <p className="mt-0.5 text-[13px] leading-snug text-white/50">
                      {organiser.bio}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Event links */}
          {links.length > 0 && (
            <div className="mt-6" data-testid="event-links">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                Links
              </h2>
              <div className="mt-2 flex flex-col gap-2">
                {links.map(({ key, label, icon: Icon, url }) => (
                  <a
                    key={key}
                    data-testid={`event-link-${key}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121212] px-4 py-3.5 transition-colors duration-200 hover:bg-[#1a1a1a]"
                  >
                    <Icon className="h-5 w-5 shrink-0 text-[#ff2a00]" />
                    <span className="flex-1 text-[15px] font-semibold text-white">
                      {label}
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0 text-white/30" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Info notice: event details can change */}
          <div
            data-testid="event-info-notice"
            className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-white/40"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Event details can change. Please check the organiser's official pages for
              updates or cancellations.
            </span>
          </div>

          {/* Public Like control (separate from the private Save above) */}
          <LikeButton eventId={event.id} variant="detail" />

          {/* Secondary actions */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              data-testid="share-btn"
              onClick={handleShare}
              className="flex items-center justify-center gap-2 rounded-full border border-white/10 bg-[#121212] py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1e1e1e] active:scale-[0.98]"
            >
              <Share2 className="h-[18px] w-[18px] text-[#ff2a00]" />
              Share
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="calendar-btn"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/10 bg-[#121212] py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1e1e1e] active:scale-[0.98]"
                >
                  <CalendarPlus className="h-[18px] w-[18px] text-[#ff2a00]" />
                  Add to Calendar
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="top"
                sideOffset={8}
                className="border-white/10 bg-[#1e1e1e] text-white"
              >
                <DropdownMenuItem
                  data-testid="calendar-google"
                  onClick={() => window.open(googleCalendarUrl(event), "_blank")}
                  className="cursor-pointer gap-2 focus:bg-white/10 focus:text-white"
                >
                  <CalendarDays className="h-4 w-4 text-[#ff2a00]" />
                  Google Calendar
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="calendar-apple"
                  onClick={() => downloadIcs(event)}
                  className="cursor-pointer gap-2 focus:bg-white/10 focus:text-white"
                >
                  <Apple className="h-4 w-4 text-[#ff2a00]" />
                  Apple / Outlook (.ics)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {canReport && (
            <div className="mt-5 flex justify-center">
              <button
                data-testid="report-event-btn"
                onClick={reportEvent}
                disabled={reporting || reported}
                className="flex items-center gap-1.5 text-xs font-medium text-white/40 transition-colors hover:text-white/70 disabled:opacity-60"
              >
                <Flag className="h-3.5 w-3.5" />
                {reported ? "Reported" : "Report event"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sticky booking bar — only when a ticket/booking link exists */}
      {showBottomBar && (
        <div className="fixed inset-x-0 bottom-0 z-[1100] border-t border-white/10 bg-[#080808] px-5 pb-7 pt-4 shadow-[0_-10px_30px_rgba(0,0,0,0.7)]">
          <div className="mx-auto max-w-md md:flex md:max-w-6xl md:justify-end md:px-6">
            <a
              data-testid="booking-link"
              href={event.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-4 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] md:w-[calc(50%-1rem)]"
            >
              <Ticket className="h-5 w-5" />
              {ticketLabel}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value, sub }) => (
  <div className="flex items-center gap-4">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1e1e1e]">
      <Icon className="h-5 w-5 text-[#ff2a00]" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/35">
        {label}
      </p>
      <p className="mt-0.5 text-[15px] font-medium text-white">{value}</p>
      {sub && <p className="mt-0.5 text-[13px] font-medium text-white/60">{sub}</p>}
    </div>
  </div>
);
